import { FiAlertCircle } from "react-icons/fi";
import { CardActionMenu } from "@/components/ui/CardActionMenu";

interface InstallmentCardProps {
  name: string;
  accountLabel: string;
  /** Palette hex (GET /accounts/colors); falls back to card-mist while it loads. */
  color?: string;
  amount: string;
  amountSuffix: string;
  nextChargeLabel?: string;
  summary: string;
  /** Interest-rate text; when null the "missing rate" chip is shown instead. */
  rateLabel: string | null;
  missingRateLabel: string;
  percent: number;
  percentLabel: string;
  progressLabel: string;
  progressValueText: string;
  menuLabel: string;
  editLabel: string;
  deleteLabel: string;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void | Promise<void>;
}

/** CuotaCard from the design system: full colored card, one color per installment. */
export const InstallmentCard = ({
  name,
  accountLabel,
  color,
  amount,
  amountSuffix,
  nextChargeLabel,
  summary,
  rateLabel,
  missingRateLabel,
  percent,
  percentLabel,
  progressLabel,
  progressValueText,
  menuLabel,
  editLabel,
  deleteLabel,
  onOpen,
  onEdit,
  onDelete,
}: InstallmentCardProps) => {
  const clamped = Math.min(100, Math.max(0, percent));

  return (
    <article
      className="relative rounded-[var(--ds-radius-lg)] p-5 text-[var(--ds-on-card)] transition-[filter] duration-150 hover:brightness-95 sm:p-6"
      style={{ backgroundColor: color ?? "var(--ds-card-mist)" }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={name}
        className="app-focus-ring absolute inset-0 rounded-[var(--ds-radius-lg)]"
      />
      <div className="pointer-events-none relative">
        <div className="flex items-start justify-between gap-3 pr-11">
          <div className="min-w-0 flex-1">
            <h3 className="break-words text-xl font-medium leading-7 sm:text-[22px]">{name}</h3>
            <p className="mt-0.5 text-[15px] leading-[22px]">{accountLabel}</p>
          </div>
          <span className="shrink-0 whitespace-nowrap">
            <span className="font-[var(--ds-font-display)] text-[28px] leading-9 tracking-[-0.03em] sm:text-[34px]">{amount}</span>
            <small className="ml-1 text-sm font-medium leading-5">{amountSuffix}</small>
          </span>
        </div>
        {nextChargeLabel ? <p className="mt-2 text-[15px] font-medium leading-[22px]">{nextChargeLabel}</p> : null}
        <p className="mt-0.5 text-[15px] leading-[22px]">{summary}</p>
        {rateLabel === null ? (
          <button
            type="button"
            onClick={onEdit}
            className="app-focus-ring pointer-events-auto relative mt-3 inline-flex h-7 items-center gap-2 rounded-full bg-[var(--ds-on-card)] px-3 text-[13px] font-medium leading-none text-[var(--ds-card-sun)]"
          >
            <FiAlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
            {missingRateLabel}
          </button>
        ) : (
          <p className="mt-0.5 text-[15px] leading-[22px]">{rateLabel}</p>
        )}
        <div className="mt-3 flex items-center gap-3">
          <span
            role="progressbar"
            aria-label={progressLabel}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(clamped)}
            aria-valuetext={progressValueText}
            className="block h-3 min-w-0 flex-1 overflow-hidden rounded-full bg-[color:color-mix(in_srgb,var(--ds-on-card)_22%,transparent)]"
          >
            <span className="block h-full rounded-full bg-[var(--ds-on-card)]" style={{ width: `${clamped}%` }} />
          </span>
          <span className="shrink-0 text-[15px] font-medium leading-5 tabular-nums">{percentLabel}</span>
        </div>
      </div>
      <div className="absolute right-3 top-3 z-20 sm:right-4 sm:top-4">
        <CardActionMenu
          name={name}
          menuLabel={menuLabel}
          actions={[
            { kind: "edit", label: editLabel, onClick: onEdit },
            { kind: "delete", label: deleteLabel, onClick: onDelete },
          ]}
        />
      </div>
    </article>
  );
};
